import { useState, useEffect } from "react";
import { Lock, Fingerprint, Eye, EyeOff } from "lucide-react";

const API = "https://wine-cellar-api.vercel.app/api/auth";

async function post(body) {
  const r = await fetch(API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return r.json();
}

function base64ToBuffer(b64) {
  const bin = atob(b64.replace(/-/g, "+").replace(/_/g, "/"));
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}
function bufferToBase64(buf) {
  return btoa(Array.from(new Uint8Array(buf), b => String.fromCharCode(b)).join("")).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export default function Auth({ onAuthenticated }) {
  const [mode, setMode] = useState("loading");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasWebAuthn, setHasWebAuthn] = useState(false);

  const inp = "w-full px-4 py-3 bg-cbg text-cpaper rounded-xl border border-cborder focus:border-cgold focus:outline-none font-sans text-sm";

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("wcm_token");
      if (token) {
        const r = await post({ action: "verify", token });
        if (r.valid) { onAuthenticated(token); return; }
        localStorage.removeItem("wcm_token");
      }
      const r = await fetch(`${API}?action=status`);
      const data = await r.json();
      setMode(data.configured ? "login" : "setup");
      const cred = await post({ action: "get_credential" });
      setHasWebAuthn(!!cred.credential);
    })();
  }, [onAuthenticated]);

  const handleSetup = async () => {
    if (!password) { setError("Please enter a password"); return; }
    if (password !== confirmPassword) { setError("Passwords don't match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    const r = await post({ action: "setup", password });
    if (r.status === "ok") {
      const lr = await post({ action: "login", password });
      localStorage.setItem("wcm_token", lr.token);
      onAuthenticated(lr.token);
    } else {
      setError(r.error || "Setup failed");
    }
    setLoading(false);
  };

  const handleLogin = async () => {
    if (!password) { setError("Please enter your password"); return; }
    setLoading(true); setError("");
    const r = await post({ action: "login", password });
    if (r.token) {
      localStorage.setItem("wcm_token", r.token);
      onAuthenticated(r.token);
    } else {
      setError("Incorrect password");
    }
    setLoading(false);
  };

  const handleBiometric = async () => {
    setLoading(true); setError("");
    try {
      const credData = await post({ action: "get_credential" });
      if (!credData.credential) { setError("No biometric registered"); setLoading(false); return; }
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          allowCredentials: [{ id: base64ToBuffer(credData.credential.id), type: "public-key" }],
          userVerification: "required",
          timeout: 60000,
        }
      });
      if (assertion) {
        const lr = await post({ action: "biometric_login" });
        if (lr.token) {
          localStorage.setItem("wcm_token", lr.token);
          onAuthenticated(lr.token);
        } else {
          setError("Biometric auth failed - please use password");
        }
      }
    } catch (e) {
      setError("Biometric failed — sign in with your password, then re-register Face ID / Touch ID from Settings (this can happen after the site's domain changes).");
    }
    setLoading(false);
  };

  const registerBiometric = async () => {
    setLoading(true); setError("");
    try {
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          rp: { name: "Wine Cellar", id: window.location.hostname },
          user: { id: crypto.getRandomValues(new Uint8Array(16)), name: "owner", displayName: "Cellar Owner" },
          pubKeyCredParams: [{ alg: -7, type: "public-key" }, { alg: -257, type: "public-key" }],
          authenticatorSelection: { userVerification: "required", residentKey: "preferred" },
          timeout: 60000,
        }
      });
      await post({
        action: "save_credential",
        credential: { id: bufferToBase64(credential.rawId), type: credential.type },
      });
      setHasWebAuthn(true);
      alert("Biometric registered! You can now use Face ID or Touch ID.");
    } catch (e) {
      setError("Biometric registration failed: " + e.message);
    }
    setLoading(false);
  };

  if (mode === "loading") return (
    <div className="min-h-screen bg-cbg flex items-center justify-center">
      <p className="text-cmuted text-sm font-sans">Loading...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-cbg flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <p className="text-cgold text-xs uppercase tracking-widest mb-3 font-sans">The Cellar</p>
          <h1 className="text-6xl font-serif text-cpaper leading-none mb-4">Ma Cave</h1>
          <p className="text-cmuted text-sm font-sans">
            {mode === "setup" ? "Create a password to get started" : "Sign in to your cellar"}
          </p>
        </div>

        {error && (
          <div className="mb-5 p-3 bg-red-900/20 border border-red-700/30 rounded-xl text-red-400 text-sm font-sans">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && (mode === "setup" ? handleSetup() : handleLogin())}
              className={inp}
            />
            <button
              onClick={() => setShowPassword(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-cmuted hover:text-cpaper transition"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {mode === "setup" && (
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSetup()}
              className={inp}
            />
          )}

          <button
            onClick={mode === "setup" ? handleSetup : handleLogin}
            disabled={loading}
            className="w-full py-3.5 bg-cgold hover:opacity-90 disabled:opacity-50 text-cpaper rounded-xl font-medium font-sans transition flex items-center justify-center gap-2"
          >
            <Lock className="w-4 h-4" />
            {loading ? "Please wait..." : mode === "setup" ? "Create Password" : "Sign In"}
          </button>

          {mode === "login" && hasWebAuthn && (
            <button
              onClick={handleBiometric}
              disabled={loading}
              className="w-full py-3.5 bg-csurf border border-cborder hover:border-cgold text-cpaper rounded-xl font-sans font-medium transition flex items-center justify-center gap-2"
            >
              <Fingerprint className="w-4 h-4" />
              Use Face ID / Touch ID
            </button>
          )}

          {mode === "login" && !hasWebAuthn && window.PublicKeyCredential && (
            <button
              onClick={registerBiometric}
              disabled={loading}
              className="w-full py-3 bg-cbg border border-cborder hover:border-cgold text-cmuted hover:text-cpaper rounded-xl font-sans text-sm transition flex items-center justify-center gap-2"
            >
              <Fingerprint className="w-4 h-4" />
              Register Face ID / Touch ID
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
